import React from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  useWearables,
  useWearableTimeseries,
  useEventWorkouts,
  // useActivitySummary, // Hidden: Whoop does not support activity/steps
  useSleepSessions,
  // useSleepSummary, // Using sessions directly — summaries have field-name mismatches with OW
  // useRecoverySummary, // Using timeseries directly — summary endpoint empty for Whoop
} from '../../hooks/useWearables';
import {
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Skeleton,
  Alert,
} from '@mantine/core';
import { IconHeart, IconActivity, IconMoon, IconBolt, IconChevronRight, IconDeviceWatch, IconScale, IconFlame } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

function formatDuration(seconds) {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function allTimeRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 12000);
  return {
    start_time: start.toISOString(),
    end_time: end.toISOString(),
    start_date: start.toISOString().split('T')[0],
    end_date: end.toISOString().split('T')[0],
  };
}

function sortDescByDate(arr, dateKey) {
  return [...arr].sort((a, b) => new Date(b[dateKey] || 0) - new Date(a[dateKey] || 0));
}

function avg(values) {
  const valid = values.filter((v) => v != null);
  if (valid.length === 0) return null;
  return valid.reduce((sum, v) => sum + v, 0) / valid.length;
}

function Dashboard() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { connections, loading: connectionsLoading } = useWearables();

  const range = allTimeRange();

  const { data: heartRateData, loading: heartRateLoading } = useWearableTimeseries({
    types: ['resting_heart_rate'],
    limit: 50,
    start_time: range.start_time,
    end_time: range.end_time,
  });

  const { workouts, loading: workoutsLoading } = useEventWorkouts({
    limit: 50,
    startDate: range.start_date,
    endDate: range.end_date,
  });

  // const { summaries: activitySummaries, loading: activityLoading } = useActivitySummary({ limit: 7 });

  const { sessions: sleepSessions, loading: sleepLoading } = useSleepSessions({
    limit: 50,
    startDate: range.start_date,
    endDate: range.end_date,
  });

  const { data: recoveryTimeseries, loading: recoveryLoading } = useWearableTimeseries({
    types: ['recovery_score', 'resting_heart_rate', 'heart_rate_variability_rmssd'],
    limit: 100,
    start_time: range.start_time,
    end_time: range.end_time,
  });

  const sortedHR = sortDescByDate(heartRateData, 'timestamp');
  const latestHR = sortedHR[0];

  const sortedSleepSessions = sortDescByDate(sleepSessions, 'start_time');
  const latestSleep = sortedSleepSessions[0];

  const latestRecoveryScore = sortDescByDate(
    (recoveryTimeseries || []).filter((d) => d.type === 'recovery_score'),
    'timestamp'
  )[0];
  const latestRecoveryHR = sortDescByDate(
    (recoveryTimeseries || []).filter((d) => d.type === 'resting_heart_rate'),
    'timestamp'
  )[0];

  const sortedWorkouts = sortDescByDate(workouts, 'start_datetime');
  const recentWorkouts = sortedWorkouts.slice(0, 5);

  const recentSleepSessions = sortedSleepSessions.slice(0, 7);
  const sleepCount = recentSleepSessions.length;
  const avgSleepDuration = avg(
    recentSleepSessions.map((s) => s.duration_seconds || s.total_sleep_time_seconds || 0)
  );

  const avgWorkouts = sortedWorkouts.slice(0, 7);
  const avgWorkoutCount = avgWorkouts.length;
  const avgWorkoutDuration = avg(avgWorkouts.map((w) => w.duration_seconds));
  const avgWorkoutCalories = avg(avgWorkouts.map((w) => w.calories_kcal));
  const avgWorkoutHR = avg(avgWorkouts.map((w) => w.avg_heart_rate_bpm));

  const hasConnections = connections.length > 0;

  if (authLoading) {
    return (
      <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
        <Stack gap="md">
          <Skeleton height={36} width={200} />
          <Grid>
            {[1, 2, 3].map((i) => (
              <Grid.Col key={i} span={{ base: 12, sm: 6, md: 4 }}>
                <Skeleton height={100} />
              </Grid.Col>
            ))}
          </Grid>
        </Stack>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
        <Card withBorder>
          <Stack gap="md">
            <Title order={2}>Dashboard</Title>
            <Text c="dimmed">Please log in to view your health data.</Text>
          </Stack>
        </Card>
      </Container>
    );
  }

  const statCardStyle = { textDecoration: 'none', cursor: 'pointer', transition: 'background 0.15s' };

  return (
    <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
      <style>{`.wearable-stat-card:hover { background-color: var(--mantine-color-pink-0) !important; }`}</style>
      <Stack gap="md">
        <div>
          <Title order={1} size="h2" style={{ marginTop: 0 }}>
            Wearables Dashboard
          </Title>
        </div>

        {!connectionsLoading && !hasConnections && (
          <Alert color="yellow" variant="light">
            <Group justify="space-between">
              <div>
                <Text fw={600}>No devices connected</Text>
                <Text size="sm" c="dimmed">Connect your wearable devices to start tracking your health</Text>
              </div>
              <Button component={Link} to="/wearables/devices" variant="light">
                Connect Devices
                <IconChevronRight size={16} style={{ marginLeft: 8 }} />
              </Button>
            </Group>
          </Alert>
        )}

        {/* --- Row 1: Stat cards --- */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card withBorder radius="md" p="md" component={Link} to="/wearables/heart-rate" className="wearable-stat-card" style={statCardStyle}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">Resting Heart Rate</Text>
                <IconHeart size={20} color="var(--mantine-color-red-6)" />
              </Group>
              {heartRateLoading ? (
                <Skeleton height={32} />
              ) : latestHR?.value ? (
                <>
                  <Text size="xl" fw={700}>{Math.round(latestHR.value)} bpm</Text>
                  <Text size="xs" c="dimmed">{formatDate(latestHR.timestamp)}</Text>
                </>
              ) : (
                <Text size="sm" c="dimmed">No data</Text>
              )}
            </Card>
          </Grid.Col>

          {/* Today's Steps card hidden: Whoop does not support activity/steps
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">Steps</Text>
                <IconActivity size={20} color="var(--mantine-color-green-6)" />
              </Group>
            </Card>
          </Grid.Col>
          */}

          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card withBorder radius="md" p="md" component={Link} to="/wearables/sleep" className="wearable-stat-card" style={statCardStyle}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">Sleep</Text>
                <IconMoon size={20} color="var(--mantine-color-indigo-6)" />
              </Group>
              {sleepLoading ? (
                <Skeleton height={32} />
              ) : latestSleep ? (
                <>
                  <Text size="xl" fw={700}>
                    {formatDuration(latestSleep.duration_seconds)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {latestSleep.efficiency_percent != null
                      ? `${Math.round(latestSleep.efficiency_percent)}% efficiency • ${formatDate(latestSleep.start_time)}`
                      : formatDate(latestSleep.start_time)}
                  </Text>
                </>
              ) : (
                <Text size="sm" c="dimmed">No data</Text>
              )}
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card withBorder radius="md" p="md" component={Link} to="/wearables/recovery" className="wearable-stat-card" style={statCardStyle}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed">Recovery</Text>
                <IconBolt size={20} color="var(--mantine-color-yellow-6)" />
              </Group>
              {recoveryLoading ? (
                <Skeleton height={32} />
              ) : latestRecoveryScore?.value != null ? (
                <>
                  <Text
                    size="xl"
                    fw={700}
                    c={latestRecoveryScore.value >= 67 ? 'green' : latestRecoveryScore.value >= 34 ? 'yellow' : 'red'}
                  >
                    {Math.round(latestRecoveryScore.value)}%
                  </Text>
                  <Text size="xs" c="dimmed">
                    {latestRecoveryHR?.value
                      ? `Resting HR: ${Math.round(latestRecoveryHR.value)} bpm • ${formatDate(latestRecoveryScore.timestamp)}`
                      : formatDate(latestRecoveryScore.timestamp)}
                  </Text>
                </>
              ) : (
                <Text size="sm" c="dimmed">No data</Text>
              )}
            </Card>
          </Grid.Col>
        </Grid>

        {/* --- Row 2: Overview cards --- */}
        <Grid gutter="md">
          {/* Weekly Activity card hidden: Whoop does not support activity/steps
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder radius="md" p="md">
              <Text fw={600} mb="xs">Weekly Activity</Text>
              <Text size="xs" c="dimmed" mb="sm">Last 7 days</Text>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Total Steps</Text>
                  <Text fw={600}>{weeklySteps.toLocaleString()}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Daily Average</Text>
                  <Text fw={600}>{activitySummaries.length ? Math.round(weeklySteps / activitySummaries.length).toLocaleString() : '0'}</Text>
                </Group>
              </Stack>
              <Button component={Link} to="/wearables/activity" variant="light" size="xs" mt="md" fullWidth>
                View Activity
                <IconChevronRight size={14} style={{ marginLeft: 4 }} />
              </Button>
            </Card>
          </Grid.Col>
          */}

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder radius="md" p="md">
              <Text fw={600} mb="xs">Sleep Average</Text>
              <Text size="xs" c="dimmed" mb="sm">Last {sleepCount} recorded night{sleepCount !== 1 ? 's' : ''}</Text>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Avg Duration</Text>
                  <Text fw={600}>{formatDuration(avgSleepDuration)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Sessions</Text>
                  <Text fw={600}>{sleepCount}</Text>
                </Group>
              </Stack>
              <Button component={Link} to="/wearables/sleep" variant="light" size="xs" mt="md" fullWidth>
                View Sleep
                <IconChevronRight size={14} style={{ marginLeft: 4 }} />
              </Button>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs">
                <Text fw={600}>Workout Average</Text>
                <IconFlame size={20} color="var(--mantine-color-orange-6)" />
              </Group>
              <Text size="xs" c="dimmed" mb="sm">Last {avgWorkoutCount} recorded workout{avgWorkoutCount !== 1 ? 's' : ''}</Text>
              {workoutsLoading ? (
                <Skeleton height={60} />
              ) : avgWorkoutCount > 0 ? (
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Avg Duration</Text>
                    <Text fw={600}>{formatDuration(avgWorkoutDuration)}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Avg Calories</Text>
                    <Text fw={600}>{avgWorkoutCalories != null ? `${Math.round(avgWorkoutCalories)} kcal` : '-'}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Avg Heart Rate</Text>
                    <Text fw={600}>{avgWorkoutHR != null ? `${Math.round(avgWorkoutHR)} bpm` : '-'}</Text>
                  </Group>
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">No workouts yet</Text>
              )}
              <Button component={Link} to="/wearables/workouts" variant="light" size="xs" mt="md" fullWidth>
                View Workouts
                <IconChevronRight size={14} style={{ marginLeft: 4 }} />
              </Button>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs">
                <Text fw={600}>Recent Workouts</Text>
                <Button component={Link} to="/wearables/workouts" variant="light" size="xs">
                  View All
                  <IconChevronRight size={14} style={{ marginLeft: 4 }} />
                </Button>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">{workouts.length} recorded workout{workouts.length !== 1 ? 's' : ''}</Text>
              {workoutsLoading ? (
                <Stack gap="xs">
                  {[1, 2, 3].map((i) => <Skeleton key={i} height={40} />)}
                </Stack>
              ) : recentWorkouts.length > 0 ? (
                <Stack gap="xs">
                  {recentWorkouts.map((workout) => (
                    <Group key={workout.id || workout.start_time} justify="space-between" p="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                      <div>
                        <Text size="sm" fw={500}>{workout.type?.replace(/_/g, ' ') || 'Workout'}</Text>
                        <Text size="xs" c="dimmed">
                          {formatDate(workout.start_datetime || workout.start_time)}
                        </Text>
                      </div>
                      <Text size="sm" c="dimmed">{formatDuration(workout.duration_seconds)}</Text>
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">No workouts yet</Text>
              )}
            </Card>
          </Grid.Col>
        </Grid>

        {/* --- Quick Links --- */}
        <Card withBorder radius="md" p="md">
          <Text fw={600} mb="xs">Quick Links</Text>
          <Text size="xs" c="dimmed" mb="md">Navigate to detailed views</Text>
          <Grid gutter="xs">
            <Grid.Col span={6} md={3}>
              <Button component={Link} to="/wearables/heart-rate" variant="light" fullWidth>
                <IconHeart size={16} style={{ marginRight: 8 }} />
                Heart Rate
              </Button>
            </Grid.Col>
            <Grid.Col span={6} md={3}>
              <Button component={Link} to="/wearables/workouts" variant="light" fullWidth>
                <IconActivity size={16} style={{ marginRight: 8 }} />
                Workouts
              </Button>
            </Grid.Col>
            <Grid.Col span={6} md={3}>
              <Button component={Link} to="/wearables/sleep" variant="light" fullWidth>
                <IconMoon size={16} style={{ marginRight: 8 }} />
                Sleep
              </Button>
            </Grid.Col>
            {/* <Grid.Col span={6} md={3}>
              <Button component={Link} to="/wearables/activity" variant="light" fullWidth>
                <IconActivity size={16} style={{ marginRight: 8 }} />
                Activity
              </Button>
            </Grid.Col> */}
            <Grid.Col span={6} md={3}>
              <Button component={Link} to="/wearables/recovery" variant="light" fullWidth>
                <IconBolt size={16} style={{ marginRight: 8 }} />
                Recovery
              </Button>
            </Grid.Col>
            <Grid.Col span={6} md={3}>
              <Button component={Link} to="/wearables/body" variant="light" fullWidth>
                <IconScale size={16} style={{ marginRight: 8 }} />
                Body
              </Button>
            </Grid.Col>
            <Grid.Col span={6} md={3}>
              <Button component={Link} to="/wearables/find-correlations" variant="light" fullWidth>
                Find Correlations
              </Button>
            </Grid.Col>
            <Grid.Col span={6} md={3}>
              <Button component={Link} to="/wearables/devices" variant="light" fullWidth>
                <IconDeviceWatch size={16} style={{ marginRight: 8 }} />
                Devices
              </Button>
            </Grid.Col>
          </Grid>
        </Card>
      </Stack>
    </Container>
  );
}

export default Dashboard;
